import { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { Button } from '@/components/ui/button.js';
import { Textarea } from '@/components/ui/textarea.js';
import { ArrowUp, X, Loader2, FileText, Plus, Image as ImageIcon, File, FileAudio, Plug, User } from 'lucide-react';
import { cn } from '@/lib/utils.js';
import { useAppContext } from '@/contexts/AppContext.js';
import type { NoteView } from '@/contexts/AppContext.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.js';
import { ConnectorSelectorModal } from '@/components/connectors/ConnectorSelectorModal.js';
import { useContactStore } from '@/stores/contactStore.js';
import type { Contact } from '@/types/contact.js';
import { SlashCommandAutocomplete } from './SlashCommandAutocomplete.js';
import { parseSlashInput } from '@/lib/slash-parser.js';
import { useAuth } from '@/contexts/AuthContext.js';
import type { Prompt } from '@/types/prompt.js';
import { CreatePromptModal } from '@/components/prompts/CreatePromptModal.js';
import { ModelPicker } from './ModelPicker.js';

interface MentionSuggestion {
  id: string;
  title: string;
  type: 'note' | 'contact';
  content: string;
  contact?: Contact;
}

interface ChatInputProps {
  onSend?: (message: string, imageData?: { base64: string; mimeType: string }, fileData?: { base64: string; mimeType: string; filename?: string }, mentionedContacts?: Contact[]) => void;
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
  variant?: 'home' | 'chat';
}

export function ChatInput({
  onSend,
  disabled = false,
  loading = false,
  placeholder = 'Message...',
  variant = 'chat',
}: ChatInputProps) {
  const { notes } = useAppContext();
  const { getAllContacts } = useContactStore();
  const { api } = useAuth();
  const [message, setMessage] = useState('');
  const [attachedNotes, setAttachedNotes] = useState<NoteView[]>([]);
  const [mentionedContacts, setMentionedContacts] = useState<Contact[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<MentionSuggestion[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Slash command state
  const [showSlashAutocomplete, setShowSlashAutocomplete] = useState(false);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [slashQuery, setSlashQuery] = useState('');
  const [showCreatePromptModal, setShowCreatePromptModal] = useState(false);

  // Attachment state
  const [imageData, setImageData] = useState<{ base64: string; mimeType: string } | null>(null);
  const [fileData, setFileData] = useState<{ base64: string; mimeType: string; filename?: string } | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const audioInputRef = useRef<HTMLInputElement>(null);
  const MAX_FILE_SIZE = 64 * 1024 * 1024; // 64MB

  // Connector state
  const [showConnectorModal, setShowConnectorModal] = useState(false);

  // Load prompts on mount
  useEffect(() => {
    const loadPrompts = async () => {
      try {
        const promptsData = await api.listPrompts();
        const promptArray = Object.values(promptsData) as Prompt[];
        setPrompts(promptArray);
      } catch (error) {
        console.error('Failed to load prompts:', error);
      }
    };
    loadPrompts();
  }, [api]);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  }, [message]);

  // Auto-focus on mount for better UX
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea && !disabled && !loading) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        textarea.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [disabled, loading]);

  // Detect slash commands
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const cursorPos = textarea.selectionStart || 0;
    const textBeforeCursor = message.slice(0, cursorPos);

    // Check if we're typing a slash command
    if (textBeforeCursor.startsWith('/') && !textBeforeCursor.includes('\n')) {
      const parsed = parseSlashInput(textBeforeCursor);
      if (parsed.isSlash) {
        setSlashQuery(textBeforeCursor);
        setShowSlashAutocomplete(true);
        setShowSuggestions(false);
        return;
      }
    } else {
      setShowSlashAutocomplete(false);
    }

    // Detect @ mentions
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.slice(lastAtIndex + 1);
      const hasSpaceAfter = textAfterAt.includes(' ');

      if (!hasSpaceAfter) {
        const query = textAfterAt.toLowerCase();

        const filteredNotes = notes
          .filter((note) => note.title.toLowerCase().includes(query))
          .map((note) => ({
            id: note.id,
            title: note.title,
            type: 'note' as const,
            content: note.content,
          }));

        const contacts = getAllContacts();
        const filteredContacts = contacts
          .filter((contact) => contact.agentCard.name.toLowerCase().includes(query))
          .map((contact) => ({
            id: contact.id,
            title: contact.agentCard.name,
            type: 'contact' as const,
            content: contact.agentCard.description,
            contact,
          }));

        const allSuggestions = [...filteredContacts, ...filteredNotes];
        setSuggestions(allSuggestions);
        setShowSuggestions(allSuggestions.length > 0);
        setSelectedIndex(0);
        return;
      }
    }

    setShowSuggestions(false);
  }, [message, notes, getAllContacts]);

  const insertMention = (suggestion: MentionSuggestion) => {
    const cursorPos = textareaRef.current?.selectionStart || 0;
    const textBeforeCursor = message.slice(0, cursorPos);
    const textAfterCursor = message.slice(cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      const before = message.slice(0, lastAtIndex);
      const mentionText = `@${suggestion.title}`;
      const newMessage = `${before}${mentionText} ${textAfterCursor}`;
      setMessage(newMessage);

      if (suggestion.type === 'note') {
        const note = notes.find((n) => n.id === suggestion.id);
        if (note && !attachedNotes.some((n) => n.id === note.id)) {
          setAttachedNotes((prev) => [...prev, note]);
        }
      } else if (suggestion.type === 'contact' && suggestion.contact) {
        if (!mentionedContacts.some((c) => c.id === suggestion.contact!.id)) {
          setMentionedContacts((prev) => [...prev, suggestion.contact!]);
        }
      }
    }

    setShowSuggestions(false);
    textareaRef.current?.focus();
  };

  const handleSelectPrompt = (prompt: Prompt) => {
    // Just insert the prompt name, don't resolve
    setMessage(`/${prompt.name} `);
    setShowSlashAutocomplete(false);

    // Focus back on textarea
    textareaRef.current?.focus();
  };

  const handleCreatePromptSuccess = async () => {
    // Reload prompts list after successful creation
    try {
      const promptsData = await api.listPrompts();
      const promptArray = Object.values(promptsData) as Prompt[];
      setPrompts(promptArray);
    } catch (error) {
      console.error('Failed to reload prompts:', error);
    }
  };

  const handleSubmit = () => {
    if ((!message.trim() && !imageData && !fileData) || disabled || loading) return;

    let finalMessage = message.trim();

    // Backend will handle slash command resolution

    if (attachedNotes.length > 0) {
      const noteContents = attachedNotes
        .map((note) => `\n\n---\nNote: ${note.title}\n${note.content}\n---`)
        .join('');
      finalMessage = `${finalMessage}${noteContents}`;
    }

    const contactsToSend = mentionedContacts.length > 0 ? mentionedContacts : undefined;

    onSend?.(finalMessage, imageData ?? undefined, fileData ?? undefined, contactsToSend);
    setMessage('');
    setAttachedNotes([]);
    setMentionedContacts([]);
    setImageData(null);
    setFileData(null);

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  // File handlers
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      alert('Image file too large. Maximum size is 64MB.');
      e.target.value = '';
      return;
    }

    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file.');
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      const commaIndex = result.indexOf(',');
      const base64 = result.substring(commaIndex + 1);
      const meta = result.substring(0, commaIndex);
      const mimeMatch = meta.match(/data:(.*);base64/);
      const mimeType = mimeMatch ? mimeMatch[1] : file.type;
      setImageData({ base64, mimeType });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handlePdfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      alert('PDF file too large. Maximum size is 64MB.');
      e.target.value = '';
      return;
    }

    if (file.type !== 'application/pdf') {
      alert('Please select a valid PDF file.');
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      const commaIndex = result.indexOf(',');
      const base64 = result.substring(commaIndex + 1);
      setFileData({ base64, mimeType: 'application/pdf', filename: file.name });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleAudioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      alert('Audio file too large. Maximum size is 64MB.');
      e.target.value = '';
      return;
    }

    if (!file.type.startsWith('audio/')) {
      alert('Please select a valid audio file.');
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      const commaIndex = result.indexOf(',');
      const base64 = result.substring(commaIndex + 1);
      setFileData({ base64, mimeType: file.type, filename: file.name });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (showSuggestions) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % suggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === 'Tab' || e.key === 'Enter') {
        if (suggestions[selectedIndex]) {
          e.preventDefault();
          insertMention(suggestions[selectedIndex]);
          return;
        }
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowSuggestions(false);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey && !showSuggestions) {
      e.preventDefault();

      // Check if user typed just "/" - open create prompt modal
      if (message.trim() === '/') {
        setShowCreatePromptModal(true);
        setMessage('');
        return;
      }

      handleSubmit();
    }
  };

  const removeAttachedNote = (noteId: string) => {
    setAttachedNotes((prev) => prev.filter((n) => n.id !== noteId));
  };

  const removeMentionedContact = (contactId: string) => {
    setMentionedContacts((prev) => prev.filter((c) => c.id !== contactId));
  };


  return (
    <div className="p-3 md:p-4 max-w-4xl mx-auto w-full space-y-3">
      {/* Attached Notes & Mentioned Contacts */}
      {(attachedNotes.length > 0 || mentionedContacts.length > 0) && (
        <div className="flex flex-wrap gap-2">
          {attachedNotes.map((note) => (
            <div
              key={note.id}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary border border-primary/20"
            >
              <FileText className="h-3.5 w-3.5" />
              <span className="text-sm font-medium">{note.title}</span>
              <button
                onClick={() => removeAttachedNote(note.id)}
                className="hover:bg-primary/20 rounded-full p-0.5 transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          {mentionedContacts.map((contact) => (
            <div
              key={contact.id}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20"
            >
              <User className="h-3.5 w-3.5" />
              <span className="text-sm font-medium">{contact.agentCard.name}</span>
              <button
                onClick={() => removeMentionedContact(contact.id)}
                className="hover:bg-purple-500/20 rounded-full p-0.5 transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* File/Image Attachments */}
      {(imageData || fileData) && (
        <div className="flex flex-wrap gap-2">
          {imageData && (
            <div className="relative w-fit border border-border rounded-lg p-1 bg-muted/50 group">
              <img
                src={`data:${imageData.mimeType};base64,${imageData.base64}`}
                alt="preview"
                className="h-16 w-auto rounded-md"
              />
              <Button
                variant="destructive"
                size="icon"
                onClick={() => setImageData(null)}
                className="absolute -top-1 -right-1 h-5 w-5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          )}
          {fileData && (
            <div className="relative w-fit border border-border rounded-lg p-2 bg-muted/50 flex items-center gap-2 group">
              {fileData.mimeType.startsWith('audio') ? (
                <FileAudio className="h-4 w-4" />
              ) : (
                <File className="h-4 w-4" />
              )}
              <span className="text-xs font-medium max-w-[160px] truncate">
                {fileData.filename || 'attachment'}
              </span>
              <Button
                variant="destructive"
                size="icon"
                onClick={() => setFileData(null)}
                className="absolute -top-1 -right-1 h-5 w-5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>
      )}

      <CreatePromptModal
        open={showCreatePromptModal}
        onOpenChange={setShowCreatePromptModal}
        onSuccess={handleCreatePromptSuccess}
      />

      {/* Layout based on variant */}
      {variant === 'home' ? (
        <div className="relative">
          {/* Slash Command Autocomplete */}
          {showSlashAutocomplete && (
            <SlashCommandAutocomplete
              isVisible={showSlashAutocomplete}
              searchQuery={slashQuery}
              prompts={prompts}
              onSelectPrompt={handleSelectPrompt}
              onClose={() => setShowSlashAutocomplete(false)}
              onCreatePrompt={() => {
                setShowCreatePromptModal(true);
                setShowSlashAutocomplete(false);
              }}
            />
          )}

          {/* @ Mention Suggestions - Positioned outside card to avoid overflow clipping */}
          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute bottom-full left-0 mb-2 w-full max-h-60 overflow-y-auto bg-popover border border-border rounded-lg shadow-lg z-50">
              {suggestions.map((suggestion, index) => (
                <button
                  key={suggestion.id}
                  onClick={() => insertMention(suggestion)}
                  className={cn(
                    'w-full flex items-start gap-3 px-4 py-3 text-left transition-colors',
                    'hover:bg-muted',
                    index === selectedIndex && 'bg-muted'
                  )}
                >
                  {suggestion.type === 'contact' ? (
                    <User className="h-5 w-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                  ) : (
                    <FileText className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm text-foreground">{suggestion.title}</p>
                      <span className={cn(
                        "text-xs px-1.5 py-0.5 rounded-full",
                        suggestion.type === 'contact'
                          ? "bg-purple-500/10 text-purple-600 dark:text-purple-400"
                          : "bg-primary/10 text-primary"
                      )}>
                        {suggestion.type}
                      </span>
                    </div>
                    {suggestion.content && (
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                        {suggestion.content}
                      </p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          <div className={cn(
            "bg-card rounded-xl border-2 border-border/60 shadow-xl transition-all duration-300",
            "hover:border-primary/40 hover:shadow-2xl hover:shadow-primary/5",
            "focus-within:border-primary/60 focus-within:ring-4 focus-within:ring-primary/10 focus-within:ring-offset-0 focus-within:shadow-2xl focus-within:shadow-primary/10",
            "backdrop-blur-sm bg-gradient-to-br from-card via-card to-card/95",
            "overflow-hidden"
          )}>
            {/* Subtle gradient overlay */}
            <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-primary/5 via-transparent to-accent/5 pointer-events-none" />

            {/* Textarea */}
            <div className="relative px-6 pt-4 z-10">
              <Textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={disabled || loading}
              className="min-h-[32px] max-h-[200px] py-2 px-0 text-base resize-none border-0 outline-none ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none bg-transparent shadow-none w-full placeholder:text-muted-foreground/60"
              rows={2}
            />
          </div>

          {/* Actions Row */}
          <div className="flex items-center justify-between px-4 pb-4 pt-1 z-10 relative">
            {/* Left: Attachments & Connectors */}
            <div className="flex items-center gap-2">
              {/* Attachments Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 rounded border border-border/50 text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-200"
                    disabled={disabled || loading}
                    title="Add attachments"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="top" align="start">
                  <DropdownMenuItem onClick={() => imageInputRef.current?.click()}>
                    <ImageIcon className="h-4 w-4 mr-2" />
                    Image
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => pdfInputRef.current?.click()}>
                    <File className="h-4 w-4 mr-2" />
                    PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => audioInputRef.current?.click()}>
                    <FileAudio className="h-4 w-4 mr-2" />
                    Audio
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Connectors Button */}
              <Button
                variant="outline"
                size="icon"
                onClick={() => setShowConnectorModal(true)}
                className="h-9 w-9 rounded border border-border/50 text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-200"
                disabled={disabled || loading}
                title="Add connectors"
              >
                <Plug className="h-4 w-4" />
              </Button>
            </div>

            {/* Right: Model Picker & Send Button */}
            <div className="flex items-center gap-2">
              <ModelPicker />

              <Button
              onClick={handleSubmit}
              disabled={(!message.trim() && !imageData && !fileData) || disabled || loading}
              size="icon-lg"
              className={cn(
                "rounded-lg transition-all duration-300 shadow-lg",
                "hover:scale-105 hover:shadow-xl active:scale-95",
                message.trim() || imageData || fileData
                  ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-primary/30"
                  : "bg-muted text-muted-foreground hover:bg-muted/80 cursor-not-allowed"
              )}
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <ArrowUp className="h-5 w-5" />
              )}
              </Button>
            </div>
          </div>
        </div>
        </div>
      ) : (
        /* Chat Layout: Inline */
        <div className="flex items-end gap-1.5 md:gap-2 bg-background p-2 rounded-lg border shadow-sm transition-all duration-200 focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary/50">
          {/* Attachments Button */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon-lg"
                className="flex-shrink-0 rounded border border-border/50 text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-200 touch-target"
                disabled={disabled || loading}
                title="Add attachments"
              >
                <Plus className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="start" className="min-w-[160px]">
              <DropdownMenuItem onClick={() => imageInputRef.current?.click()} className="py-3 md:py-2">
                <ImageIcon className="h-5 w-5 md:h-4 md:w-4 mr-3 md:mr-2" />
                Image
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => pdfInputRef.current?.click()} className="py-3 md:py-2">
                <File className="h-5 w-5 md:h-4 md:w-4 mr-3 md:mr-2" />
                PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => audioInputRef.current?.click()} className="py-3 md:py-2">
                <FileAudio className="h-5 w-5 md:h-4 md:w-4 mr-3 md:mr-2" />
                Audio
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Connectors Button - hidden on mobile for cleaner look */}
          <Button
            variant="outline"
            size="icon-lg"
            onClick={() => setShowConnectorModal(true)}
            className="flex-shrink-0 rounded border border-border/50 text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-200 hidden md:flex touch-target"
            disabled={disabled || loading}
            title="Add connectors"
          >
            <Plug className="h-5 w-5" />
          </Button>

          {/* Textarea */}
          <div className="flex-1 relative py-2">
            {/* Slash Command Autocomplete */}
            {showSlashAutocomplete && (
              <SlashCommandAutocomplete
                isVisible={showSlashAutocomplete}
                searchQuery={slashQuery}
                prompts={prompts}
                onSelectPrompt={handleSelectPrompt}
                onClose={() => setShowSlashAutocomplete(false)}
                onCreatePrompt={() => {
                  setShowCreatePromptModal(true);
                  setShowSlashAutocomplete(false);
                }}
              />
            )}

            {/* @ Mention Suggestions */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute bottom-full left-0 mb-2 w-full max-h-60 overflow-y-auto bg-popover border border-border rounded-lg shadow-lg z-50">
                {suggestions.map((suggestion, index) => (
                  <button
                    key={suggestion.id}
                    onClick={() => insertMention(suggestion)}
                    className={cn(
                      'w-full flex items-start gap-3 px-4 py-3 text-left transition-colors',
                      'hover:bg-muted',
                      index === selectedIndex && 'bg-muted'
                    )}
                  >
                    {suggestion.type === 'contact' ? (
                      <User className="h-5 w-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                    ) : (
                      <FileText className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm text-foreground">{suggestion.title}</p>
                        <span className={cn(
                          "text-xs px-1.5 py-0.5 rounded-full",
                          suggestion.type === 'contact'
                            ? "bg-purple-500/10 text-purple-600 dark:text-purple-400"
                            : "bg-primary/10 text-primary"
                        )}>
                          {suggestion.type}
                        </span>
                      </div>
                      {suggestion.content && (
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                          {suggestion.content}
                        </p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}

            <Textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={disabled || loading}
              className="min-h-[24px] max-h-[200px] py-0 px-0 text-base resize-none border-0 focus-visible:ring-0 bg-transparent shadow-none w-full"
              rows={1}
            />
          </div>

          {/* Model Picker & Send Button */}
          <div className="flex items-center gap-2">
            <ModelPicker />

            <Button
            onClick={handleSubmit}
            disabled={(!message.trim() && !imageData && !fileData) || disabled || loading}
            size="icon-lg"
            className={cn(
              "flex-shrink-0 rounded transition-all duration-200 touch-target",
              message.trim() || imageData || fileData ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"
            )}
          >
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <ArrowUp className="h-5 w-5" />
            )}
            </Button>
          </div>
        </div>
      )}

      {/* Hidden file inputs */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleImageChange}
      />
      <input
        ref={pdfInputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={handlePdfChange}
      />
      <input
        ref={audioInputRef}
        type="file"
        accept="audio/*"
        className="hidden"
        onChange={handleAudioChange}
      />

      {/* Connector Selector Modal */}
      <ConnectorSelectorModal
        open={showConnectorModal}
        onClose={() => setShowConnectorModal(false)}
      />
    </div>
  );
}
